import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DASH } from "../constants/testIds";
import {
  Users, CalendarCheck, Airplane, Clock, TrendUp, Cake, Star, MegaphoneSimple,
  Confetti, HandsClapping, ArrowRight, MapPin, Calendar,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const CHART_COLORS = ["#1a4d8f", "#2ec7c8", "#e04698", "#8ec73f", "#1e6fb8", "#f97316", "#a78bfa", "#eab308"];
const PULSE = { 1: { emoji: "🥲", label: "Rough" }, 2: { emoji: "😕", label: "Meh" }, 3: { emoji: "😐", label: "OK" }, 4: { emoji: "🙂", label: "Good" }, 5: { emoji: "😄", label: "Great" } };

function StatCard({ label, value, sub, icon: Icon, testid, accent, trend }) {
  return (
    <div data-testid={testid} className="border border-zinc-200 bg-white p-5 rounded-sm hover:border-adb-navy/40 hover:shadow-sm transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
          <div className="mt-2 font-display text-4xl font-bold tracking-tight text-adb-navy">{value}</div>
          {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
        </div>
        <div className={`w-11 h-11 grid place-items-center rounded-sm ${accent || "bg-adb-navy/5 text-adb-navy"}`}>
          <Icon size={22} weight="duotone" />
        </div>
      </div>
      {trend != null && (
        <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-1 text-xs">
          <TrendUp size={12} className={trend >= 0 ? "text-emerald-700" : "text-red-700 rotate-180"} />
          <span className={trend >= 0 ? "text-emerald-700" : "text-red-700"}>{trend >= 0 ? "+" : ""}{trend}%</span>
          <span className="text-zinc-500">vs last week</span>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, tenantId } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get("/dashboard/summary", { params }).then(({ data }) => setSummary(data)).finally(() => setLoading(false));
  }, [tenantId, user?.role]);

  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  const canFetch = user?.role !== "super_admin" || tenantId;

  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <div data-testid={DASH.root} className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Command center</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-zinc-950 mt-1">
            {greeting}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            {summary?.personal ? "Here's your day at a glance ·" : "Here's what's happening across your workforce today ·"} {new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}.
          </p>
        </div>
        <QuickActions />
      </div>

      {/* Row 1: Stats */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="border border-zinc-200 bg-white p-5 rounded-sm h-32 animate-pulse" />)}
        </div>
      ) : summary?.personal ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="dash-personal-stats">
          <StatCard testid={DASH.statEmployees} label="Leave balance" value={summary.leave_remaining} sub="Days remaining this year" icon={Airplane} accent="bg-gradient-to-br from-zenith-blue-500/10 to-zenith-blue/5 text-zenith-blue-500 border border-zenith-blue-500/20" />
          <StatCard testid={DASH.statPresent} label="Days present" value={summary.present_month} sub={summary.checked_in_today ? "Checked in today" : "Not checked in yet today"} icon={CalendarCheck} accent="bg-gradient-to-br from-zenith-teal/10 to-zenith-teal/5 text-zenith-teal border border-zenith-teal/25" />
          <StatCard testid={DASH.statOnLeave} label="Hours this week" value={summary.hours_week} sub={summary.logged_today ? "Today's hours logged" : "No hours logged today"} icon={Clock} accent="bg-gradient-to-br from-zenith-magenta/10 to-zenith-magenta/5 text-zenith-magenta border border-zenith-magenta/25" />
          <StatCard testid={DASH.statPending} label="My pending requests" value={summary.pending_mine} sub="Leaves & claims awaiting decision" icon={Users} accent="bg-gradient-to-br from-zenith-green/15 to-zenith-green/5 text-zenith-green-700 border border-zenith-green/30" />
        </div>
      ) : summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard testid={DASH.statEmployees} label="Total employees" value={summary.total_employees} sub={`Across ${summary.total_departments} departments`} icon={Users} accent="bg-gradient-to-br from-zenith-blue-500/10 to-zenith-blue/5 text-zenith-blue-500 border border-zenith-blue-500/20" />
          <StatCard testid={DASH.statPresent} label="Checked in today" value={summary.checked_in_today} sub="Live · updates every 30s" icon={CalendarCheck} accent="bg-gradient-to-br from-zenith-teal/10 to-zenith-teal/5 text-zenith-teal border border-zenith-teal/25" />
          <StatCard testid={DASH.statOnLeave} label="On leave today" value={summary.on_leave_today} sub="Approved absences" icon={Airplane} accent="bg-gradient-to-br from-zenith-magenta/10 to-zenith-magenta/5 text-zenith-magenta border border-zenith-magenta/25" />
          <StatCard testid={DASH.statPending} label="Pending approvals" value={summary.pending_leaves} sub="Leaves awaiting decision" icon={Clock} accent="bg-gradient-to-br from-zenith-green/15 to-zenith-green/5 text-zenith-green-700 border border-zenith-green/30" />
        </div>
      )}

      {/* Row 2: charts + celebrations */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-zinc-200 bg-white rounded-sm p-5 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{summary.personal ? "My week · Last 7 days" : "Attendance · Last 7 days"}</div>
                <div className="font-display text-xl font-bold tracking-tight mt-1">{summary.personal ? "Hours logged per day" : "Daily present headcount"}</div>
              </div>
              <TrendUp size={22} className="text-adb-navy" />
            </div>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.attendance_trend}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="date" stroke="#71717a" fontSize={11} tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { weekday: "short", day: "2-digit" })} />
                  <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#f4f4f5" }} contentStyle={{ borderRadius: 2, border: "1px solid #e4e4e7", fontSize: 12 }} />
                  <Bar dataKey={summary.personal ? "hours" : "present"} fill="#1a2d59" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{summary.personal ? "My leave" : "Department mix"}</div>
            <div className="font-display text-xl font-bold tracking-tight mt-1">{summary.personal ? "Remaining days by type" : "Headcount by department"}</div>
            <div className="mt-2 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={summary.department_breakdown} dataKey="count" nameKey="department" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {summary.department_breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #e4e4e7", fontSize: 12 }} />
                  <Legend verticalAlign="bottom" iconType="square" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Row 3: interactive widgets grid */}
      {canFetch && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <PulseWidget />
          <CelebrationsWidget params={params} />
          <KudosPreview params={params} />
        </div>
      )}

      {canFetch && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ApprovalsInboxPreview params={params} />
          <TimeOffCalendar params={params} />
          <AnnouncementsPreview params={params} />
        </div>
      )}
    </div>
  );
}

// ----------- Quick actions -----------
function QuickActions() {
  return (
    <div className="flex flex-wrap gap-2">
      <Link to="/app/checkin" data-testid="qa-checkin" className="px-3 py-2 bg-adb-navy hover:bg-adb-navy-800 text-white rounded-sm text-xs font-semibold flex items-center gap-1.5 transition-colors">
        <MapPin size={14} weight="fill" /> Check-in
      </Link>
      <Link to="/app/leaves" data-testid="qa-leave" className="px-3 py-2 border border-zinc-300 hover:border-adb-navy hover:bg-white text-zinc-800 rounded-sm text-xs font-semibold flex items-center gap-1.5 transition-colors">
        <Airplane size={14} /> Apply leave
      </Link>
      <Link to="/app/payslips" data-testid="qa-payslips" className="px-3 py-2 border border-zinc-300 hover:border-adb-navy hover:bg-white text-zinc-800 rounded-sm text-xs font-semibold flex items-center gap-1.5 transition-colors">
        <Calendar size={14} /> Payslips
      </Link>
    </div>
  );
}

// ----------- Pulse -----------
function PulseWidget() {
  const [state, setState] = useState({ this_week: null, history: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/pulse/mine").then(({ data }) => setState(data)).catch(() => {}); }, []);

  const submit = async (level) => {
    setBusy(true);
    try {
      await api.post("/pulse", { level });
      const { data } = await api.get("/pulse/mine");
      setState(data);
      toast.success(`Recorded: ${PULSE[level].emoji} ${PULSE[level].label}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const done = !!state.this_week;

  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="dash-pulse">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-sm bg-adb-green/10 grid place-items-center"><HandsClapping size={16} className="text-adb-navy" weight="duotone" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Weekly pulse</div>
          <div className="font-display text-base font-bold tracking-tight">How's your week going?</div>
        </div>
      </div>
      {done ? (
        <div className="mt-4 text-center py-3 bg-adb-green/5 border border-adb-green/30 rounded-sm">
          <div className="text-4xl">{PULSE[state.this_week.level]?.emoji}</div>
          <div className="text-xs text-zinc-600 mt-1">Thanks — logged as <span className="font-semibold">{PULSE[state.this_week.level]?.label}</span></div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-5 gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              disabled={busy}
              onClick={() => submit(n)}
              data-testid={`pulse-${n}`}
              className="border border-zinc-200 hover:border-adb-navy hover:bg-adb-navy/5 rounded-sm py-2 transition-all duration-150 hover:scale-105"
            >
              <div className="text-2xl">{PULSE[n].emoji}</div>
              <div className="text-[9px] text-zinc-500 mt-0.5">{PULSE[n].label}</div>
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 text-[10px] text-zinc-400 flex items-center gap-1"><Star size={9} /> Anonymous · once per week</div>
    </div>
  );
}

// ----------- Celebrations -----------
function CelebrationsWidget({ params }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/celebrations", { params: { ...params, days: 30 } }).then(({ data }) => setRows(data)).catch(() => {}); }, [params.tenant_id]);
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="dash-celebrations">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-sm bg-orange-100 grid place-items-center"><Confetti size={16} className="text-orange-600" weight="duotone" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Next 30 days</div>
          <div className="font-display text-base font-bold tracking-tight">Celebrations 🎉</div>
        </div>
      </div>
      <div className="mt-3 space-y-1.5 max-h-[220px] overflow-y-auto">
        {rows.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">No celebrations coming up.</div>}
        {rows.slice(0, 8).map((r, i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-zinc-50 transition-colors">
            <div className="text-lg">{r.kind === "birthday" ? "🎂" : "🎊"}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{r.employee_name}</div>
              <div className="text-[10px] text-zinc-500 font-mono-plex">{r.date} {r.kind === "anniversary" && r.years && `· ${r.years}-yr`}</div>
            </div>
            <div className="text-[10px] uppercase font-semibold text-zinc-400">{r.kind === "birthday" ? "B'day" : "Work"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------- Kudos preview -----------
function KudosPreview({ params }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/kudos", { params }).then(({ data }) => setRows(data)).catch(() => {}); }, [params.tenant_id]);
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="dash-kudos">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm bg-adb-navy/10 grid place-items-center"><Star size={16} className="text-adb-navy" weight="duotone" /></div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Recent kudos</div>
            <div className="font-display text-base font-bold tracking-tight">Peer recognition</div>
          </div>
        </div>
        <Link to="/app/kudos" className="text-[10px] uppercase tracking-widest text-adb-navy font-semibold hover:underline" data-testid="dash-kudos-all">All →</Link>
      </div>
      <div className="mt-3 space-y-2 max-h-[220px] overflow-y-auto">
        {rows.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">No kudos yet — be the first!</div>}
        {rows.slice(0, 4).map((r) => (
          <div key={r.id} className="border border-zinc-100 rounded-sm p-2.5 bg-gradient-to-br from-adb-green/5 to-transparent">
            <div className="text-xs text-zinc-700 line-clamp-2">"{r.message}"</div>
            <div className="text-[10px] text-zinc-500 mt-1"><span className="font-semibold text-adb-navy">{r.from_name}</span> → <span className="font-semibold">{r.to_name}</span> {r.value_tag && <span className="ml-1 px-1.5 py-0.5 bg-adb-navy/10 text-adb-navy rounded-sm">#{r.value_tag}</span>}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------- Approvals inbox preview -----------
function ApprovalsInboxPreview({ params }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  useEffect(() => { api.get("/approvals-inbox", { params }).then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); }).catch(() => {}); }, [params.tenant_id]);
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="dash-approvals">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm bg-blue-50 grid place-items-center"><Clock size={16} className="text-blue-700" weight="duotone" /></div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Approvals inbox</div>
            <div className="font-display text-base font-bold tracking-tight">{total} pending</div>
          </div>
        </div>
        <Link to="/app/approvals-inbox" className="text-[10px] uppercase tracking-widest text-adb-navy font-semibold hover:underline" data-testid="dash-approvals-all">Open →</Link>
      </div>
      <div className="mt-3 space-y-1.5 max-h-[220px] overflow-y-auto">
        {rows.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">Nothing pending 🎯</div>}
        {rows.slice(0, 5).map((r) => (
          <Link key={r.id} to={r.route} className="block border border-zinc-100 rounded-sm p-2 hover:border-adb-navy transition-colors">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-zinc-800">{r.title}</div>
              <ArrowRight size={12} className="text-zinc-400" />
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">{r.sub} · <span className="font-semibold">{r.by}</span></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ----------- Time-off calendar -----------
function TimeOffCalendar({ params }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/time-off-calendar", { params: { ...params, days: 14 } }).then(({ data }) => setRows(data)).catch(() => {}); }, [params.tenant_id]);
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="dash-timeoff">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-sm bg-orange-50 grid place-items-center"><Airplane size={16} className="text-orange-600" weight="duotone" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Next 30 days</div>
          <div className="font-display text-base font-bold tracking-tight">Who's away</div>
        </div>
      </div>
      <div className="mt-3 space-y-1.5 max-h-[220px] overflow-y-auto">
        {rows.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">Everyone's on deck.</div>}
        {rows.slice(0, 6).map((r) => (
          <div key={r.id} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-zinc-50">
            <Cake size={12} className="text-orange-500" />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{r.employee_name}</div>
              <div className="text-[10px] text-zinc-500 font-mono-plex">{r.leave_type} · {r.start_date} → {r.end_date}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------- Announcements preview -----------
function AnnouncementsPreview({ params }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/announcements", { params }).then(({ data }) => setRows(data)).catch(() => {}); }, [params.tenant_id]);
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="dash-announcements">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-sm bg-adb-green/10 grid place-items-center"><MegaphoneSimple size={16} className="text-adb-navy" weight="duotone" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Company feed</div>
          <div className="font-display text-base font-bold tracking-tight">Announcements</div>
        </div>
      </div>
      <div className="mt-3 space-y-2 max-h-[220px] overflow-y-auto">
        {rows.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">No announcements yet.</div>}
        {rows.slice(0, 4).map((r) => (
          <div key={r.id} className="border border-zinc-100 rounded-sm p-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs font-semibold text-zinc-800 line-clamp-1">{r.title}</div>
              {r.pinned && <span className="text-[9px] uppercase tracking-widest text-adb-green font-bold">Pinned</span>}
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5 line-clamp-2">{r.body}</div>
            <div className="text-[9px] text-zinc-400 mt-1 font-mono-plex">{r.author_name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
